/**
 * Utilitário para geocodificação usando o Nominatim (OpenStreetMap).
 *
 * Limite da API pública: 1 requisição/segundo.
 * Para uso interno (poucos usuários simultâneos) isso é mais que suficiente.
 */

const NOMINATIM_BASE = 'https://nominatim.openstreetmap.org';

let lastRequestTime = 0;

async function throttleRequest<T>(fn: () => Promise<T>): Promise<T> {
  const now = Date.now();
  const elapsed = now - lastRequestTime;

  if (elapsed < 1100) {
    await new Promise((resolve) => setTimeout(resolve, 1100 - elapsed));
  }

  lastRequestTime = Date.now();
  return fn();
}

export interface ReverseGeocodeResult {
  address: string | null;
  neighborhood: string | null;
  city: string | null;
  state: string | null;
}

/**
 * Reverse geocode: coordenadas → endereço.
 */
export async function reverseGeocode(
  latitude: number,
  longitude: number
): Promise<ReverseGeocodeResult> {
  return throttleRequest(async () => {
    try {
      const url = `${NOMINATIM_BASE}/reverse?format=json&lat=${latitude}&lon=${longitude}&accept-language=pt-BR`;
      const response = await fetch(url, {
        headers: { 'User-Agent': 'crm-iluminacao-publica/1.0' },
      });

      if (!response.ok) {
        return { address: null, neighborhood: null, city: null, state: null };
      }

      const data = await response.json();
      const addr = data?.address || {};

      const road = addr.road || '';
      const houseNumber = addr.house_number || '';
      const neighbourhood =
        addr.suburb || addr.quarter || addr.neighbourhood || null;
      const city =
        addr.city || addr.town || addr.municipality || null;
      const state = addr.state || null;

      const addressParts: string[] = [];
      if (road) {
        addressParts.push(houseNumber ? `${road}, ${houseNumber}` : road);
      }
      if (city) addressParts.push(city);
      if (state) addressParts.push(state);

      return {
        address: addressParts.length > 0 ? addressParts.join(' - ') : null,
        neighborhood: neighbourhood,
        city,
        state,
      };
    } catch {
      return { address: null, neighborhood: null, city: null, state: null };
    }
  });
}

export interface ForwardGeocodeResult {
  latitude: number;
  longitude: number;
  displayName: string;
}

/**
 * Forward geocode: endereço → coordenadas.
 */
export async function forwardGeocode(
  query: string
): Promise<ForwardGeocodeResult[]> {
  return throttleRequest(async () => {
    try {
      const params = new URLSearchParams({
        format: 'json',
        q: query,
        countrycodes: 'br',
        limit: '5',
        'accept-language': 'pt-BR',
      });

      const url = `${NOMINATIM_BASE}/search?${params.toString()}`;
      const response = await fetch(url, {
        headers: { 'User-Agent': 'crm-iluminacao-publica/1.0' },
      });

      if (!response.ok) return [];

      const data = await response.json();

      return (data as Array<Record<string, unknown>>).map(
        (item: Record<string, unknown>) => ({
          latitude: parseFloat(String(item.lat)),
          longitude: parseFloat(String(item.lon)),
          displayName: String(item.display_name || ''),
        })
      );
    } catch {
      return [];
    }
  });
}

/**
 * Busca bairro a partir de coordenadas (função específica usada no Orders.tsx).
 */
export async function fetchNeighborhoodFromCoords(
  latitude: number,
  longitude: number
): Promise<string | null> {
  const result = await reverseGeocode(latitude, longitude);
  return result.neighborhood;
}